/**
 * ReportingKpis — the stat-tile row at the top of the Reporting tab, fed by
 * GET /api/audit/analytics/overview. Same tile language as the rest of the
 * app: white rounded-xl card, micro-label, one big number, one quiet subline.
 */
import React from 'react';
import { useAnalytics, fmtPct, fmtNum } from './ReportingShared.jsx';
import { STAGE_LABELS, SEVERITY_LABELS } from './labels.js';

const SEV_TEXT = { critical: 'text-red-600', high: 'text-orange-600', medium: 'text-amber-600', low: 'text-slate-500', unspecified: 'text-slate-400' };
const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'unspecified'];
const STAGE_ORDER = ['setup', 'engagement', 'data_integrity', 'fieldwork', 'reporting'];

function Tile({ label, value, sub, valueClass = 'text-slate-800' }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-xl font-bold mt-1 ${valueClass}`}>{value}</div>
      <div className="text-[10px] text-slate-400 mt-0.5 leading-4 min-h-[16px]">{sub}</div>
    </div>
  );
}

export default function KpiRow({ qs }) {
  const { loading, error, data } = useAnalytics('overview', qs);
  if (loading) return <div className="bg-white border border-slate-200 rounded-xl px-4 py-6 text-xs text-slate-400">Adding up the key numbers…</div>;
  if (error) return <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-xs text-red-600">{error}</div>;

  const o = data?.overview || {};
  const byStage = o.audits_by_stage || {};
  const stageBits = STAGE_ORDER
    .filter(s => Number(byStage[s]) > 0)
    .map(s => `${byStage[s]} ${STAGE_LABELS[s] || s}`)
    .join(' · ');

  const bySev = o.open_findings_by_severity || {};
  const openFindings = Object.values(bySev).reduce((t, n) => t + Number(n || 0), 0);
  const sevBits = SEV_ORDER.filter(s => Number(bySev[s]) > 0);

  const g = o.grounded_finding_rate || {};
  const overdue = Number(o.overdue_caps || 0);
  const backlog = Number(o.needs_human_backlog || 0);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Tile label="Active audits" value={fmtNum(o.active_audits ?? 0)} sub={stageBits || 'None in flight right now'} />
      <Tile label="Completed" value={fmtNum(o.completed_in_range ?? 0)} sub="In the selected period" />
      <Tile label="Avg compliance score" value={o.avg_compliance_score === null || o.avg_compliance_score === undefined ? 'n/a' : `${o.avg_compliance_score}%`} sub="Across scored audits" />
      <Tile
        label="Open findings" value={fmtNum(openFindings)}
        sub={sevBits.length
          ? sevBits.map((s, i) => (
              <span key={s} className={SEV_TEXT[s]}>{i > 0 && <span className="text-slate-300"> · </span>}{bySev[s]} {(SEVERITY_LABELS[s] || s).toLowerCase()}</span>
            ))
          : 'Nothing open'}
      />
      <Tile label="Overdue CAPs" value={fmtNum(overdue)} valueClass={overdue > 0 ? 'text-red-600' : 'text-slate-800'} sub={overdue > 0 ? 'Past their due date' : 'Nothing past due'} />
      <Tile label="Universe retries" value={fmtNum(o.multi_attempt_universes ?? 0)} sub="Audits that needed 2 or more submissions" />
      <Tile label="Grounded findings" value={fmtPct(g.rate)} sub={g.total ? `${g.grounded} of ${g.total} tied to verbatim evidence` : 'No findings yet'} />
      <Tile label="Awaiting human review" value={fmtNum(backlog)} valueClass={backlog > 0 ? 'text-amber-600' : 'text-slate-800'} sub={backlog > 0 ? 'Conclusions waiting on a reviewer' : 'Review queue is clear'} />
    </div>
  );
}
